import React, { useEffect, useMemo, useState } from 'react';
import Slider from 'react-slick';
import SectionHeader from './ui/SectionHeader';
import useRevealOnScroll from '../hooks/useRevealOnScroll';

const roomsData = [
  {
    title: 'One Queen Bed',
    rate: 75,
    roomId: 'room2',
    blurb: 'A practical and comfortable room ideal for solo travelers or couples.',
    previewImage: '/images/queen/1.jpg',
    images: ['/images/queen/1.jpg', '/images/queen/2.jpg', '/images/queen/3.jpg', '/images/queen/4.jpg', '/images/queen/5.jpg', '/images/queen/6.jpg', '/images/queen/7.jpg', '/images/queen/8.jpg']
  },
  {
    title: 'Two Double Beds',
    rate: 85,
    roomId: 'room1',
    blurb: 'Great for friends or small families who need extra sleeping space.',
    previewImage: '/images/double/1.jpg',
    images: ['/images/double/1.jpg', '/images/double/2.jpg', '/images/double/3.jpg', '/images/double/4.jpg', '/images/double/5.jpg', '/images/double/6.jpg', '/images/double/7.jpg', '/images/double/8.jpg']
  },
  {
    title: 'Jacuzzi Room',
    rate: 120,
    roomId: 'room3',
    blurb: 'A premium option with additional space and a relaxing in-room jacuzzi.',
    previewImage: '/images/jet/1.jpg',
    images: ['/images/jet/1.jpg', '/images/jet/2.jpg', '/images/jet/3.jpg', '/images/jet/4.jpg', '/images/jet/5.jpg']
  }
];

const modalSettings = {
  dots: true,
  infinite: true,
  speed: 600,
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true
};

const RoomModal = ({ room, onClose }) => {
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${room.title} photo gallery`}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-[#111827cc] px-4 py-8"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="card-surface relative w-full max-w-4xl p-4 md:p-8">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close room photos"
          className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-brand-border text-brand-primary transition-colors hover:bg-[#efe6d7] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent"
        >
          <span className="text-2xl leading-none">&times;</span>
        </button>

        <h3 className="mb-6 pr-12 font-heading text-3xl text-brand-primary">{room.title}</h3>
        <div className="modal-carousel">
          <Slider {...modalSettings}>
            {room.images.map((img, index) => (
              <div key={img}>
                <img src={img} alt={`${room.title} view ${index + 1}`} className="h-[50vh] w-full rounded-xl object-cover" />
              </div>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

const RoomCard = ({ title, rate, roomId, images, blurb, previewImage, onOpen }) => {
  const hasImages = images.length > 0;

  return (
    <article className="card-surface overflow-hidden transition-all duration-300 hover:-translate-y-1">
      <img src={previewImage} alt={`${title} preview`} className="h-56 w-full object-cover" />
      <div className="p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h3 className="font-heading text-3xl text-brand-primary">{title}</h3>
          <span className="rounded-full border border-brand-border bg-[#f5efe2] px-3 py-1 text-sm font-semibold text-brand-primary">
            From ${rate}
          </span>
        </div>
        <p className="text-brand-muted leading-relaxed">{blurb}</p>
        <button
          type="button"
          onClick={() => onOpen(roomId)}
          disabled={!hasImages}
          className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-50"
        >
          View Photos
        </button>
      </div>
    </article>
  );
};

const RoomsSection = () => {
  const [activeRoomId, setActiveRoomId] = useState(null);
  const { ref, isVisible } = useRevealOnScroll();

  const activeRoom = useMemo(() => roomsData.find((room) => room.roomId === activeRoomId), [activeRoomId]);

  return (
    <section id="rooms" className="section-shell">
      <div ref={ref} className={`card-surface p-8 md:p-12 ${isVisible ? 'reveal-visible' : ''} reveal`}>
        <SectionHeader
          eyebrow="Stay Options"
          title="Rooms designed for straightforward comfort"
          subtitle="Choose the room that fits your trip. Every room includes modern essentials and clean, practical layouts."
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {roomsData.map((room) => (
            <RoomCard key={room.roomId} {...room} onOpen={setActiveRoomId} />
          ))}
        </div>

        <p className="mt-8 text-sm text-brand-muted">Rates are starting prices per night. Applicable taxes and additional fees may apply.</p>
      </div>

      {activeRoom ? <RoomModal room={activeRoom} onClose={() => setActiveRoomId(null)} /> : null}
    </section>
  );
};

export default RoomsSection;
